'use client';

import { addUserPres, updateUserPres } from '@/api/account';
import {
  EDIT_PRESCRIPTION_SUB_STEPS,
  PRESCRIPTION_METHODS,
  ZL_PRESCRIPTION_DATA,
} from '@/app/_lens-components/constants';
import { useDefaultSelection } from '@/app/_lens-components/DefaultSelectionContext';
import {
  type PrescriptionsMethod,
  useLensProcessStore,
} from '@/app/_lens-components/store/lens-process-store';
import { CustomerPrescription, ILoadPR } from '@/app/_lens-components/types';
import {
  getApiPrescriptionType,
  preIsSame,
  removePrescriptionPlus,
} from '@/app/_lens-components/utils';
import LearnPrescriptionDialog, {
  PrescriptionParamType,
} from '@/app/account/my-prescriptions/add-prescription/components/learn-prescription-dialog';
import { IPrescriptionOptions } from '@/components/PrescriptionFormV2/types';
import UnionDialog from '@/components/PrescriptionFormV2/UnionDialog';
import {
  checkPrescription,
  getEmptyPrescription,
  getOptions,
  validateUnion,
} from '@/components/PrescriptionFormV2/utils';
import { toast } from '@/components/Toast';
import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { EPrescriptionType } from '@/utils/enums/goods';
import { hasTwoPd } from '@/utils/prescription';
import { setSession } from '@/utils/tools';
import { useTranslations } from '@/i18n/use-translations';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import ScanPrescription from './components/ScanPrescription';
import PrescriptionCorrectionDialog from './components/PrescriptionCorrectionDialog';
import FillOutPrescriptionMethods from './components/FillOutPrescriptionMethods';
import PrescriptionConfirmDialog from './components/PrescriptionConfirmDialog';
import FillOutPrescriptionMain from './components/FillOutPrescriptionMain';
import SavedPrescription from './components/SavedPrescription';
import useTrackEditPrescription from './services/useTrackEditPrescription';
import {
  mergePrevLensSelection,
  readPrevLensSelection,
} from '@/app/_lens-components/utils/operatePrevLensSelection';
import { usePrevious } from 'ahooks';
import useResponsive from '@/hooks/useResponsive';

interface EditPrescriptionProps {
  prescriptionType: EPrescriptionType;
  onSubmitPrescription: (data: any, pres?: CustomerPrescription) => void;
}

// 定义组件ref类型
export interface EditPrescriptionRef {
  confirmPrescription: () => Promise<void>;
  confirmChosenPrescription: () => void;
  getSelectedPrescription: () => CustomerPrescription;
  confirmSelectMethodsFromFooter: () => void;
}

const EditPrescription = forwardRef<EditPrescriptionRef, EditPrescriptionProps>(
  ({ prescriptionType, onSubmitPrescription }, ref) => {
    const { hasDefaultSelection, canModifyPrevSelection } = useDefaultSelection();
    const t = useTranslations('lens-process');
    const currency = useStore((state) => state.currency);
    const token = useStore((state) => state.token);
    const userInfo = useStore((state) => state.userInfo);
    const publicResourceData = useStore((state) => state.publicResourceData);
    const setShowLoginRegisterDialog = useStore((state) => state.setShowLoginRegisterDialog);
    const setIsGlobalLoading = useStore((state) => state.setIsGlobalLoading);
    const { trackEvent } = useReporting();
    const {
      setPrescriptionType,
      setPrescription,
      presTypeOptionName,
      lensGoodsItem,
      lensGoodsSensorsData,
      scanInfo,
      goodsFramePriceInfo,
      selectedMethod,
      setSelectedMethod,
      editPresStep,
      setEditPresStep,
      setScanInfo,
      setLensGoodsSensorsData,
      setNowStep,
      nowStep,
      selectedPrescription,
      setSelectedPrescription,
      prescriptionFillMethodsData,
    } = useLensProcessStore();
    const [showCorrectionDialog, setShowCorrectionDialog] = useState(false);
    const [learnPrescriptionTab, setLearnPrescriptionTab] = useState<PrescriptionParamType>('sph');
    const [scannedPrescriptionUrl, setScannedPrescriptionUrl] = useState('');
    const [isFromScanToForm, setIsFromScanToForm] = useState(false);
    const { isMobile } = useResponsive();
    const prevPresStep = usePrevious(editPresStep);
    const {
      trackSubmitSavedPrescription,
      trackPrescriptionMethodsSelect,
      trackPrescriptionMethodsNextClick,
    } = useTrackEditPrescription();

    const [prLoadAnimationTime, setPrLoadAnimationTime] = useState(15);
    useEffect(() => {
      const {
        jsonvalues: {
          prescription_recognition_open: { load_animation_time },
        },
      } = publicResourceData;
      setPrLoadAnimationTime(load_animation_time);
    }, [publicResourceData]);

    useEffect(() => {
      if (!hasDefaultSelection || editPresStep !== EDIT_PRESCRIPTION_SUB_STEPS.SELECT_METHODS) {
        return;
      }
      if (selectedMethod) return;

      const { methods, prescriptionTypesCount } = prescriptionFillMethodsData;
      if (!methods?.length) return;

      const getPrescriptionTypeKey = (presTypeArg: EPrescriptionType) => {
        if (presTypeArg === EPrescriptionType.PRESCRIPTION_READING) {
          return EPrescriptionType.READING_GLASSES;
        }
        return presTypeArg;
      };
      const presType = getPrescriptionTypeKey(prescriptionType);
      const prescriptionMethods = methods.slice().sort((a, b) => a.status - b.status);
      const myPrescriptionMethodOrder = prescriptionMethods.findIndex(
        (item) => item.type === PRESCRIPTION_METHODS.CHOOSE_PRESCRIPTION,
      );
      if (myPrescriptionMethodOrder !== -1 && prescriptionTypesCount[presType] === 0) {
        prescriptionMethods.push(prescriptionMethods.splice(myPrescriptionMethodOrder, 1)[0]);
      }
      if (!token || !userInfo?.id) {
        const idx = prescriptionMethods.findIndex(
          (item) => item.type === PRESCRIPTION_METHODS.CHOOSE_PRESCRIPTION,
        );
        if (idx !== -1) {
          prescriptionMethods.splice(idx, 1);
        }
      }

      const isDisabled = (item: PrescriptionsMethod) => {
        if (item.type === PRESCRIPTION_METHODS.CHOOSE_PRESCRIPTION) {
          return (prescriptionTypesCount[presType] || 0) === 0;
        }
        return item.status === 2;
      };

      const prevMethod = readPrevLensSelection().prevFillPrescriptionMethod;
      const isEnabled = (m: PrescriptionsMethod) => !isDisabled(m);
      const pick =
        (prevMethod && prescriptionMethods.find((m) => m.type === prevMethod && isEnabled(m))) ||
        prescriptionMethods.find(
          (m) => m.type === PRESCRIPTION_METHODS.SCAN_PRESCRIPTION && isEnabled(m),
        ) ||
        prescriptionMethods.find(isEnabled);
      if (pick) setSelectedMethod(pick);
    }, [
      hasDefaultSelection,
      editPresStep,
      prescriptionFillMethodsData,
      prescriptionType,
      token,
      userInfo?.id,
      selectedMethod,
      setSelectedMethod,
    ]);

    // ===== 处方相关状态 =====
    const [prescriptionOptions, setPrescriptionOptions] = useState<IPrescriptionOptions>(
      () => getEmptyPrescription() as IPrescriptionOptions,
    );
    const [saveMyPres, setSaveMyPres] = useState(false); // 是否保存处方

    // ===== 弹窗相关状态 =====
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [learnPrescriptionDialogOpen, setLearnPrescriptionDialogOpen] = useState(false);
    const [unionDialogOpen, setUnionDialogOpen] = useState(false);
    const savedPrescriptionRef = useRef<{ selectedPres: CustomerPrescription | null }>(null);
    // ===== 表单控制 =====
    const { control, setValue, trigger, getValues, setError, clearErrors, reset } = useForm({
      defaultValues: {
        prescription: JSON.stringify(getEmptyPrescription()),
        prescriptionName: '',
      },
    });

    // ===== 登录相关函数 =====
    const handleOpenLogin = (eventType: string, situation?: string) => {
      setShowLoginRegisterDialog({
        show: true,
        eventType,
        ...(situation && { situation }),
      });
    };

    // ===== 处方数据处理函数 =====
    const scrollToError = () => {
      const errorElement = document.querySelector('.form-error-text');
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    // 处方表单选项打开回调
    const handlePrescriptionSelectionOpen = (key: string) => {
      SDPrescriptionEnterPageParamValueClick(key);
    };

    // 处理保存处方选项变更
    const handleSaveMyPresChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;
      // * 如果选择了保存的处方，则更新选中处方的isUpdate状态
      if (selectedPrescription) {
        setSelectedPrescription({ ...selectedPrescription, isUpdate: checked });
      }
      setSaveMyPres(checked);

      if (checked && !token) {
        handleOpenLogin('prescription', '保存我的处方');
      }
    };

    // 用户在选择处方后是否有修改
    const isChangedViaCompareUserPres = () => {
      if (!selectedPrescription || !selectedPrescription.id) return false;
      const selectedPrescriptionData = selectedPrescription.prescription || {};
      const selectedPrescriptionKeys = Object.keys(selectedPrescriptionData);
      // 检查每个键的值是否相同
      for (const key of selectedPrescriptionKeys) {
        if (prescriptionOptions[key] !== selectedPrescriptionData[key]) {
          return true;
        }
      }
      return getValues('prescriptionName') !== selectedPrescription.name;
    };

    // 保存处方到用户账户
    const savePrescriptionToAccount = async (prescriptionData: IPrescriptionOptions) => {
      if (!saveMyPres) return;

      try {
        setIsGlobalLoading(true);
        const submitData = {
          ...removePrescriptionPlus({ ...prescriptionData }),
          prescription_type: getApiPrescriptionType(prescriptionType),
          name: getValues('prescriptionName'),
          prescriptions_image: scanInfo?.imageUrl || '',
        };
        // 若没有扫描处方+选择了用户处方，并且用户没有修改处方，则使用用户处方的图片
        if (
          !submitData.prescriptions_image &&
          selectedPrescription?.id &&
          !isChangedViaCompareUserPres()
        ) {
          // @ts-ignore
          submitData.prescriptions_image = selectedPrescription?.prescriptions_image || '';
        }

        if (selectedPrescription?.id) {
          const { code, msg } = await updateUserPres(selectedPrescription.id, submitData);
          if (code !== 200) {
            toast.error(msg || t('failedToSavePrescription'));
          }
        } else {
          const addData = { ...submitData, pre_scan_id: undefined, scene: 2 };
          if (
            preIsSame(prescriptionData, {
              ...getEmptyPrescription(),
              ...selectedPrescription?.prescription,
            }) &&
            scanInfo?.recordId
          ) {
            addData.pre_scan_id = scanInfo.recordId;
          }
          const { code, msg } = await addUserPres(addData);
          if (code !== 200) {
            const nameError = msg || t('failedToSavePrescription');
            toast.error(nameError);
            setError('prescriptionName', {
              message: nameError,
            });
            return false;
          }
        }
        return true;
      } catch (error) {
        logger.error('Failed to save prescription', error);
        toast.error(error.msg || t('failedToSavePrescription'));
        return false;
      } finally {
        setIsGlobalLoading(false);
      }
    };

    const checkAdd = (presType: string, _odAdd: string, _osAdd: string) => {
      let odAdd = _odAdd || '0.00';
      let osAdd = _osAdd || '0.00';
      if (presType == EPrescriptionType.PROGRESSIVE) {
        // ②处方类型为Progressive时，仅有一侧的ADD值在可选范围时，OD、OS均使用此ADD值
        // ③处方类型为Progressive时，左右眼ADD值均在可选范围且值不同时，ADD值忽略不填
        if (odAdd != osAdd) {
          if (odAdd != '0.00' && osAdd != '0.00') {
            odAdd = '0.00';
            osAdd = '0.00';
          } else if (odAdd != '0.00') {
            osAdd = odAdd;
          } else {
            odAdd = osAdd;
          }
        }
      } else if (
        presType == EPrescriptionType.SINGLE_VISION ||
        presType == EPrescriptionType.SUNGLASSES_PRESCRIPTION
      ) {
        // ④处方类型为Sunglasses Prescription时，ADD值忽略不填
        odAdd = '';
        osAdd = '';
      }
      return { odAdd, osAdd };
    };

    const handleScanPrescription = (data: ILoadPR) => {
      const { prescription: scanPrescription, presType, prescriptions_image } = data;
      logger.info(prescriptions_image, 'prescriptions_image');
      logger.warn('handleScanPrescription', scanPrescription);
      if (!scanPrescription.pd && !scanPrescription.pd_l && !scanPrescription.pd_r) {
        // 识别出的pd结果为空，pd的勾选状态和当前页面的pd勾选状态保持一致
        // TODO 这里实际不会生效，因为单双PD在给表单组件传递处方时表单组件会重新进行判断。这里必要性不大暂时不处理
        scanPrescription.hasTwoPd = prescriptionOptions.hasTwoPd;
      }
      if (presType && presType !== prescriptionType) {
        setPrescriptionType(presType as EPrescriptionType);
      }
      // add值校验
      const _presType = presType || prescriptionType;
      const { odAdd, osAdd } = checkAdd(
        _presType,
        scanPrescription.od_add,
        scanPrescription.os_add,
      );
      scanPrescription.od_add = odAdd;
      scanPrescription.os_add = osAdd;

      // CYL值为0时，忽略AXIS，要在cyl值校验之后再判断axis
      if (
        scanPrescription.od_cyl == 'SPH/DS' ||
        scanPrescription.od_cyl == '0.00' ||
        !scanPrescription.od_cyl
      ) {
        scanPrescription.od_axis = 'None';
      }
      if (
        scanPrescription.os_cyl == 'SPH/DS' ||
        scanPrescription.os_cyl == '0.00' ||
        !scanPrescription.os_cyl
      ) {
        scanPrescription.os_axis = 'None';
      }

      if (prescriptions_image) {
        setScannedPrescriptionUrl(prescriptions_image);
      }
      setEditPresStep(EDIT_PRESCRIPTION_SUB_STEPS.FILL_PRESCRIPTION_FORM);
      loadPrescription(
        {
          prescription: { ...getEmptyPrescription(), ...scanPrescription },
          name: '',
        },
        _presType,
      );
      setIsFromScanToForm(true);
    };

    /**
     * 根据处方类型缓存处方信息
     */
    const getCachePres = () => {
      const key = ZL_PRESCRIPTION_DATA;
      const cacheValue = localStorage.getItem(key);
      try {
        logger.warn('getCachePres====', cacheValue, prescriptionType);
        if (cacheValue) {
          const data = JSON.parse(cacheValue);
          if (data[prescriptionType]) {
            if (data[prescriptionType].pd && Number(data[prescriptionType].pd) > 0) {
              data[prescriptionType].pd = Number(data[prescriptionType].pd).toFixed(2);
            }
            if (data[prescriptionType].recordId) {
              setSession('saveScannedPres', {
                ...data[prescriptionType],
                pre_scan_scene_type: 'cache_use',
              });
              delete data[prescriptionType].recordId;
            }

            logger.warn('getCachePres====', data[prescriptionType]);

            loadPrescription({ prescription: data[prescriptionType], name: '' });
          }
        }
      } catch (e) {
        localStorage.removeItem(key);
        logger.warn('getCachePres====', e, key);
      }
    };
    const scrollToTop = () => {
      if (isMobile) {
        window.scrollTo({
          top: 0,
          behavior: 'instant',
        });
      } else {
        const rightPart = document.querySelector('#right-part');
        if (rightPart) {
          rightPart.scrollTo({
            top: 0,
            behavior: 'instant',
          });
        }
      }
    };
    useEffect(() => {
      if (editPresStep !== EDIT_PRESCRIPTION_SUB_STEPS.FILL_PRESCRIPTION_FORM) {
        setSelectedPrescription(null);
      }
      if (
        prevPresStep !== EDIT_PRESCRIPTION_SUB_STEPS.CHOOSE_PRESCRIPTION &&
        editPresStep === EDIT_PRESCRIPTION_SUB_STEPS.FILL_PRESCRIPTION_FORM
      ) {
        // 如果上一部不是选择处方，则清空保存处方状态和选中处方
        setSaveMyPres(false);
      }
      if (editPresStep === EDIT_PRESCRIPTION_SUB_STEPS.SELECT_METHODS) {
        setScanInfo(null);
      }
      // 如果从扫描处方到表单，则不使用缓存处方数据，也不需要进行重置
      if (isFromScanToForm || prevPresStep === EDIT_PRESCRIPTION_SUB_STEPS.CHOOSE_PRESCRIPTION) {
        return scrollToTop();
      }
      const isNeedCacheData = editPresStep === EDIT_PRESCRIPTION_SUB_STEPS.FILL_PRESCRIPTION_FORM;

      if (isNeedCacheData) {
        getCachePres();
      } else {
        const emptyPrescription = getEmptyPrescription();
        reset({
          prescription: JSON.stringify(emptyPrescription),
          prescriptionName: '',
        });
        // 同步更新 prescriptionOptions 状态，确保内层表单也能重置
        setPrescriptionOptions(emptyPrescription);
      }
    }, [editPresStep, isFromScanToForm, prevPresStep]);

    useEffect(() => {
      if (selectedPrescription) {
        setValue('prescriptionName', selectedPrescription.name);
        setSaveMyPres(selectedPrescription.isUpdate || false);
      } else {
        setSaveMyPres(false);
        setValue('prescriptionName', '');
      }
    }, [selectedPrescription]);
    /**
     * 设置当前表单缓存
     */
    const setCachePres = (presValue: IPrescriptionOptions, recordId?: string) => {
      const key = ZL_PRESCRIPTION_DATA;
      const cacheValue = localStorage.getItem(key);
      try {
        const data = cacheValue ? JSON.parse(cacheValue) : {};
        data[prescriptionType] = { ...presValue };
        data[prescriptionType].saveMyPres = false;
        data[prescriptionType].name = '';
        data[prescriptionType].id = '';
        data[prescriptionType].prescriptions_image = '';
        if (recordId) {
          data[prescriptionType].recordId = recordId;
        }
        localStorage.setItem(key, JSON.stringify(data));
      } catch (error) {
        logger.warn('setCachePres====', error, key);
        localStorage.removeItem(key);
      }
    };

    // 加载选择的处方
    const loadPrescription = (pres: CustomerPrescription, presType?: string) => {
      // 使用getEmptyPrescription作为基础，确保所有必选字段都存在
      const emptyPrescription = getEmptyPrescription();
      // 合并处方数据
      const prescription = { ...emptyPrescription, ...pres.prescription };
      checkPrescription(prescription, {
        presType: presType || prescriptionType,
        prescription: {
          is_two_pd: hasTwoPd(prescription),
        },
        usePd: {
          min_pd: Number(lensGoodsItem?.product?.pd_min) || 54,
          max_pd: Number(lensGoodsItem?.product?.pd_max) || 78,
        },
      });
      logger.warn('loadPrescription====', prescription);
      setPrescriptionOptions(prescription);
      setValue('prescription', JSON.stringify(prescription));
      setTimeout(() => {
        trigger('prescription');
      });
    };

    // ===== 确认处方相关函数 =====

    // 确认处方数据
    const confirmPrescription = async () => {
      let isValid = await trigger();
      const data = getValues();
      logger.log('confirmPrescription', isValid);
      if (!isValid) {
        setTimeout(() => {
          scrollToError();
        });
        return;
      }

      // 处理表单数据
      const formData = JSON.parse(data.prescription) as IPrescriptionOptions;

      isValid = validateUnion(formData, getApiPrescriptionType(prescriptionType));
      if (!isValid) {
        setUnionDialogOpen(true);
        return;
      }

      // 如果用户选择保存处方
      if (saveMyPres) {
        if (!token) {
          handleOpenLogin('prescription', '保存我的处方');
          return;
        }
      }

      setPrescriptionOptions(formData);
      // 设置处方数据并打开确认弹窗
      setIsConfirmDialogOpen(true);

      SD_PrescriptionEnterPage_Submit_Click(JSON.stringify(formData));
    };

    // 确认处方选择
    const confirm = async () => {
      try {
        const flag = await savePrescriptionToAccount(prescriptionOptions);
        if (flag === false) {
          setIsConfirmDialogOpen(false);
          return;
        }
      } catch (error) {
        logger.error('保存处方失败', error);
      }

      // 缓存处方到本地
      setCachePres(prescriptionOptions);

      setPrescription(prescriptionOptions);
      // * 如果是点了编辑处方按钮进入的填写处方页面
      const pres =
        prevPresStep === EDIT_PRESCRIPTION_SUB_STEPS.CHOOSE_PRESCRIPTION
          ? savedPrescriptionRef.current?.selectedPres
          : null;
      // 将处方数据发送给父组件
      onSubmitPrescription(
        {
          ...prescriptionOptions,
        },
        pres,
      );

      // 发送GA事件
      trackEvent(
        {
          eventName: 'prescription_modal_confirm',
          properties: {
            event_category: 'ADDPRESCRIPTION',
            event_label: prescriptionType,
          },
        },
        [ProviderType.GA4],
      );

      // 关闭确认弹窗
      setIsConfirmDialogOpen(false);
    };

    // 验证处方参数是否在有效范围内
    const validatePrescriptionRange = (prescription: any) => {
      const prescriptionContext = {
        presType: prescriptionType || 'Single Vision',
        prescription: {
          is_two_pd: prescription.is_two_pd || false,
        },
        usePd: {
          min_pd: Number(lensGoodsItem?.product?.pd_min) || 54,
          max_pd: Number(lensGoodsItem?.product?.pd_max) || 78,
        },
      };

      const validKeys = [
        'od_sph',
        'os_sph',
        'od_cyl',
        'os_cyl',
        'od_add',
        'os_add',
        'od_axis',
        'os_axis',
        'pd',
        'pd_l',
        'pd_r',
      ];

      for (const key of validKeys) {
        const value = prescription[key];

        // 跳过空值或未定义的值
        if (value === null || value === undefined || value === '') {
          continue;
        }

        // 特殊处理 ADD 字段：对于非渐进镜片类型，ADD 字段可以为任何值（包括无效值）
        if (key.includes('add')) {
          const isProgressive = prescriptionContext.presType === 'Progressive';
          const isBifocal = prescriptionContext.presType === 'Bifocal';

          // 对于单光镜片和太阳镜处方，ADD 字段不需要验证
          if (!isProgressive && !isBifocal) {
            continue;
          }
        }

        // 特殊处理 PD 字段：既要验证数值范围，也要验证是否在选项列表中
        if (key === 'pd' || key === 'pd_l' || key === 'pd_r') {
          const pdValue = parseFloat(String(value));
          if (isNaN(pdValue)) {
            logger.warn(`PD 参数 ${key} 不是有效数字: ${value}`);
            return false;
          }

          const { min_pd } = prescriptionContext.usePd;
          const { max_pd } = prescriptionContext.usePd;

          if (key === 'pd') {
            // 单 PD：验证整个 PD 值是否在范围内
            if (pdValue < min_pd || pdValue > max_pd) {
              logger.warn(`单 PD 参数 ${key} 超出范围: ${value}，有效范围: ${min_pd} - ${max_pd}`);
              return false;
            }
          } else if (key === 'pd_l' || key === 'pd_r') {
            // 双 PD：验证左右眼 PD 之和是否在范围内
            const otherPdKey = key === 'pd_l' ? 'pd_r' : 'pd_l';
            const otherPdValue = parseFloat(String(prescription[otherPdKey])) || 0;
            const totalPd = pdValue + otherPdValue;

            if (totalPd < min_pd || totalPd > max_pd) {
              logger.warn(
                `双 PD 总和超出范围: ${key}(${pdValue}) + ${otherPdKey}(${otherPdValue}) = ${totalPd}，有效范围: ${min_pd} - ${max_pd}`,
              );
              return false;
            }
          }

          // PD 字段额外校验：检查是否在 getOptions 返回的选项列表中
          const validOptions = getOptions(key, prescriptionContext) as string[];
          if (!validOptions.includes(String(value))) {
            // 对于数字字段，还要检查带正号的版本（如 +1.00）
            const valueWithPlus = `+${value}`;
            if (!validOptions.includes(valueWithPlus)) {
              logger.warn(
                `PD 参数 ${key} 不在有效选项列表中: ${value}，有效选项: [${validOptions.slice(0, 5).join(', ')}...]`,
              );
              return false;
            }
          }
        } else {
          // 其他字段：使用选项列表验证
          const validOptions = getOptions(key, prescriptionContext) as string[];

          // 检查值是否在有效选项中
          if (!validOptions.includes(String(value))) {
            // 对于数字字段，还要检查带正号的版本（如 +1.00）
            const valueWithPlus = `+${value}`;
            if (!validOptions.includes(valueWithPlus)) {
              logger.warn(
                `处方参数 ${key} 超出范围: ${value}，有效范围: [${validOptions.slice(0, 5).join(', ')}...]`,
              );
              return false;
            }
          }
        }
      }

      return true;
    };

    const handleSelectPrescription = (isUpdate: boolean) => {
      const pres = savedPrescriptionRef.current?.selectedPres;
      if (!pres) return;
      const newPres = isUpdate ? { ...pres, isUpdate } : pres;
      setSelectedPrescription(newPres);
      loadPrescription(pres);
      setValue('prescriptionName', pres.name);
      setSaveMyPres(true);
      return pres;
    };
    const onEditPrescription = () => {
      const pres = handleSelectPrescription(true);
      if (!pres) return;
      setEditPresStep(EDIT_PRESCRIPTION_SUB_STEPS.FILL_PRESCRIPTION_FORM);
    };
    const confirmChosenPrescription = () => {
      const pres = handleSelectPrescription(false);
      logger.info(pres, 'prescription');
      if (!pres) return;

      // 验证处方参数是否有无效参数, 如果有，进入编辑我的处方环节
      const isValidPrescription = validatePrescriptionRange(pres.prescription);

      if (!isValidPrescription) {
        setEditPresStep(EDIT_PRESCRIPTION_SUB_STEPS.FILL_PRESCRIPTION_FORM);
      } else {
        onSubmitPrescription(pres.prescription, pres);
        setNowStep(nowStep + 1);
      }

      trackSubmitSavedPrescription(pres.name, 'Next');
    };

    const confirmSelectMethodsFromFooter = () => {
      if (editPresStep !== EDIT_PRESCRIPTION_SUB_STEPS.SELECT_METHODS) return;
      if (!selectedMethod) return;
      setLensGoodsSensorsData({
        ...lensGoodsSensorsData,
        prescription_enter_type: selectedMethod.title,
      });
      trackPrescriptionMethodsNextClick(selectedMethod.title);
      if (canModifyPrevSelection) {
        mergePrevLensSelection({ prevFillPrescriptionMethod: selectedMethod.type ?? '' });
      }
      setEditPresStep(selectedMethod.type);
    };
    // ===== 副作用 =====
    // 暴露方法给父组件
    useImperativeHandle(ref, () => ({
      confirmPrescription,
      confirmChosenPrescription,
      getSelectedPrescription: () => selectedPrescription,
      confirmSelectMethodsFromFooter,
    }));

    // ===== 埋点 =====

    function SDPrescriptionEnterPageParamValueClick(param_value_type: string) {
      try {
        trackEvent(
          {
            eventName: 'PrescriptionEnterPage_ParamValue_Click',
            properties: {
              ...(lensGoodsSensorsData || {}),
              prescription_type_name: presTypeOptionName,
              prescription_type: getApiPrescriptionType(prescriptionType),
              is_magnification_page: false,
              param_value_type,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.error('👀', e);
      }
    }

    function SD_PrescriptionEnterPage_Submit_Click(user_prescription_detail: string) {
      try {
        trackEvent(
          {
            eventName: 'PrescriptionEnterPage_Submit_Click',
            properties: {
              ...(lensGoodsSensorsData || {}),
              prescription_type_name: presTypeOptionName,
              prescription_type: getApiPrescriptionType(prescriptionType),
              user_prescription_detail,
              frame_price: parseFloat(goodsFramePriceInfo?.price),
              current_frame_price: parseFloat(goodsFramePriceInfo?.sell_price),
              currency: currency.name,
              btn_name:
                selectedMethod?.type === EDIT_PRESCRIPTION_SUB_STEPS.CHOOSE_PRESCRIPTION
                  ? 'Submit'
                  : 'Next',
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.error('👀', e);
      }
    }
    function SDPrescriptionEnterPagePrescriptionIntroductionClick(
      btn_name: string,
      prescription: IPrescriptionOptions,
    ) {
      trackEvent(
        {
          eventName: 'PrescriptionEnterPage_PrescriptionIntroduction_Click',
          properties: {
            ...(lensGoodsSensorsData || {}),
            prescription_type_name: presTypeOptionName,
            prescription_type: getApiPrescriptionType(prescriptionType),
            user_prescription_detail: JSON.stringify(prescription),
            btn_name,
            is_magnification_page: false,
          },
        },
        [ProviderType.SENSORS],
      );
    }

    const onSelectMethod = (method: PrescriptionsMethod) => {
      setSelectedMethod(method);
      if (canModifyPrevSelection) {
        mergePrevLensSelection({ prevFillPrescriptionMethod: method.type ?? '' });
      }
      if (hasDefaultSelection) {
        return;
      }
      setLensGoodsSensorsData({
        ...lensGoodsSensorsData,
        prescription_enter_type: method.title,
      });
      trackPrescriptionMethodsSelect(method.title);

      setEditPresStep(method.type);
    };
    const onClickSignIn = () => {
      setShowLoginRegisterDialog(true);
    };
    const handleLearnPrescriptionDialogClose = () => {
      setLearnPrescriptionDialogOpen(false);
      setLearnPrescriptionTab('sph');
    };
    const subSteps = [
      {
        key: EDIT_PRESCRIPTION_SUB_STEPS.SELECT_METHODS,
        component: (
          <FillOutPrescriptionMethods
            prescriptionType={prescriptionType}
            onSelectMethod={onSelectMethod}
            onClickSignIn={onClickSignIn}
            isStepActive={editPresStep === EDIT_PRESCRIPTION_SUB_STEPS.SELECT_METHODS}
          />
        ),
      },
      {
        key: EDIT_PRESCRIPTION_SUB_STEPS.CHOOSE_PRESCRIPTION,
        component: (
          <SavedPrescription
            prescriptionType={prescriptionType}
            isStepActive={editPresStep === EDIT_PRESCRIPTION_SUB_STEPS.CHOOSE_PRESCRIPTION}
            onEditPrescription={onEditPrescription}
            ref={savedPrescriptionRef}
          />
        ),
      },
      {
        key: EDIT_PRESCRIPTION_SUB_STEPS.PRESCRIPTION_RECOGNITION,
        component: (
          <ScanPrescription
            prescriptionType={prescriptionType}
            presTypeOptionName={presTypeOptionName}
            prLoadAnimationTime={prLoadAnimationTime}
            onLoadPrescription={handleScanPrescription}
          />
        ),
      },
      {
        key: EDIT_PRESCRIPTION_SUB_STEPS.FILL_PRESCRIPTION_FORM,
        component: (
          <FillOutPrescriptionMain
            prescriptionType={prescriptionType}
            prescriptionOptions={prescriptionOptions}
            control={control}
            trigger={trigger}
            isFromScan={isFromScanToForm}
            uploadedS3Url={scannedPrescriptionUrl}
            saveMyPres={saveMyPres}
            lensGoodsItem={lensGoodsItem}
            isStepActive={editPresStep === EDIT_PRESCRIPTION_SUB_STEPS.FILL_PRESCRIPTION_FORM}
            clearErrors={clearErrors}
            onPrescriptionSelectionOpen={handlePrescriptionSelectionOpen}
            onSaveMyPresChange={handleSaveMyPresChange}
            onModifyIsFromScan={setIsFromScanToForm}
            onLearnPrescriptionClick={(tab?: PrescriptionParamType) => {
              tab && setLearnPrescriptionTab(tab);
              setLearnPrescriptionDialogOpen(true);
            }}
          />
        ),
      },
    ];
    return (
      <div className={`edit-prescription-area h-full overflow-hidden`}>
        <div className="h-full w-full">
          {subSteps.map(({ key, component }) => (
            <div key={key} className={`${editPresStep === key ? 'block' : 'hidden'}`}>
              {component}
            </div>
          ))}
        </div>

        {/* 确认处方数据 */}
        <PrescriptionConfirmDialog
          open={isConfirmDialogOpen}
          prescriptionOptions={prescriptionOptions}
          prescriptionType={prescriptionType}
          onClose={() => {
            setIsConfirmDialogOpen(false);
            SDPrescriptionEnterPagePrescriptionIntroductionClick('Close', prescriptionOptions);
          }}
          onConfirm={() => {
            confirm();
            SDPrescriptionEnterPagePrescriptionIntroductionClick('Confirm', prescriptionOptions);
          }}
          onEdit={() => {
            setIsConfirmDialogOpen(false);
            SDPrescriptionEnterPagePrescriptionIntroductionClick('Edit', prescriptionOptions);
          }}
        />

        {/* 学习处方弹窗 */}
        <LearnPrescriptionDialog
          open={learnPrescriptionDialogOpen}
          initTab={learnPrescriptionTab}
          onClose={handleLearnPrescriptionDialogClose}
        />
        <UnionDialog
          unionShow={unionDialogOpen}
          onOk={() => setUnionDialogOpen(false)}
          onClose={() => setUnionDialogOpen(false)}
        />
        <PrescriptionCorrectionDialog
          open={showCorrectionDialog}
          onCancel={() => {
            setShowCorrectionDialog(false);
          }}
          onContinue={() => {}}
        />
      </div>
    );
  },
);

export default EditPrescription;
